<template>
  <div>
    <div class="line-item-title select-all">{{ lineItem.title }}</div>
    <div class="line-item-gallery mr-3 d-flex flex-column">
      <div :class="`line-item-thumbnail ${lineItem.designed ? 'designed' : ''}`" :data-id="lineItem.identify" @click="handleLineItemClick">
        <i class="ti ti-circle-check-filled text-success"></i>
        <Image :src="$util.addQueryParam(lineItem?.thumbnail, { size: 'xs' })" />
      </div>
      <div v-if="lineItem.gallery?.length > 0" class="gallery d-flex flex-wrap">
        <div v-for="(image, index) in lineItem.gallery.slice(0, 3)" :key="index" class="gallery-item thumbnail" @click="handlePreviewClick">
          <Image :src="$util.addQueryParam(image, { size: 'thumbnail' })" v-if="index < 2 || lineItem.gallery.length <= 3" />
          <div v-else class="more-thumbnail">+{{ lineItem.gallery.length - 2 }}</div>
        </div>
      </div>
    </div>
    <div class="line-item-main">
      <div class="line-item-summary">
        <div class="line-item-id">
          <label class="mb-0">Identify: </label>
          <span class="select-all">{{ lineItem.external_identify }}</span>
        </div>
        <div v-if="lineItem?.product_id && lineItem.product_id != lineItem.external_identify" class="line-item-id">
          <label class="mb-0">Product ID: </label>
          <span class="select-all">{{ lineItem.product_id }}</span>
        </div>
        <div v-if="lineItem.sale_detail?.more?.sku" class="line-item-id">
          <label class="mb-0">Sku: </label>
          <span class="select-all">{{ lineItem.sale_detail?.more?.sku }}</span>
        </div>
        <div class="line-item-quantity d-flex align-items-center">
          <label class="pr-2 mb-0">Quantity: </label>
          <span v-if="!editable?.quantity" class="select-all" :class="{ 'text-danger font-weight-bold': lineItem.quantity > 1 }">{{ lineItem.quantity }}</span>
          <div v-else class="position-relative" style="width: 80px">
            <input class="form-control" type="number" name="line_item[quantity]" v-model="lineItem.quantity" />
          </div>
        </div>
        <div v-if="parseFloat(lineItem?.sale_detail?.price) > 0" class="line-item-price">
          <div v-if="lineItem.sale_detail?.unit_price > 0" class="mb-0">
            Unit Price: {{ lineItem.sale_detail.unit_price }}
            <span class="currency">{{ lineItem?.sale_detail?.currency || '-' }}</span>
          </div>
          <div v-if="lineItem.sale_detail?.shipping_fee > 0" class="mb-0">
            Shipping Price: {{ lineItem.sale_detail.shipping_fee }}
            <span class="currency">{{ lineItem?.sale_detail?.currency || '-' }}</span>
          </div>
          <div v-if="lineItem.sale_detail?.tax_amount > 0" class="mb-0">
            Tax Price: {{ lineItem.sale_detail.tax_amount }}
            <span class="currency">{{ lineItem?.sale_detail?.currency || '-' }}</span>
          </div>
          <div v-if="lineItem.sale_detail?.discount_amount > 0" class="mb-0">
            Discount Price: {{ lineItem.sale_detail.discount_amount }}
            <span class="currency">{{ lineItem?.sale_detail?.currency || '-' }}</span>
          </div>
          <div v-if="lineItem.sale_detail?.price > 0" class="mb-0">
            Total Price: {{ lineItem.sale_detail.price }}
            <span class="currency">{{ lineItem?.sale_detail?.currency || '-' }}</span>
          </div>
        </div>
        <div v-if="!editable?.order" :class="`flag ${lineItem?.status} ${lineItem?.design_flag} ${editable?.order || ''}`">
          <span>{{ lineItem.status_label }}</span>
        </div>
        <div class="reference mt-2">
          <a v-if="lineItem?.design_url" target="_blank" :href="lineItem.design_url">Design</a>
          <a v-if="lineItem?.manufacture_url" target="_blank" :href="lineItem.manufacture_url">Manufacture</a>
        </div>
      </div>
      <div class="line-item-meta">
        <template v-if="lineItem.attributes && Object.keys(lineItem.attributes).length > 0">
          <div v-for="(value, attribute) in lineItem.attributes" :key="attribute" :class="`meta-group ${attribute}`">
            <label class="text-capitalize">{{ lineItem.attributes_name?.[attribute]?.label || attribute }}: </label>
            <span class="select-all" v-html="$util.autoLink(value)"></span>
          </div>
        </template>
        <div v-if="lineItem.note" class="meta-group note">
          <label>Note: </label>
          <ul>
            <li v-for="(note, index) in lineItem.note" :key="index" v-html="$util.autoLink(note)"></li>
          </ul>
        </div>
      </div>
      <div v-if="lineItem?.vendor_mapping" class="design-vendor-mapping">
        <div class="vendor-mapping-inner">
          <div class="vendor-mapping-group account-name">
            <label>Account: </label>
            <span>{{ lineItem.vendor_mapping.vendor_name }}</span>
          </div>
          <div class="vendor-mapping-group account-server">
            <label>Platform: </label>
            <span>{{ lineItem.vendor_mapping?.platform_label || lineItem.vendor_mapping.platform }}</span>
          </div>
          <div v-if="lineItem.vendor_mapping?.id" class="vendor-mapping-group account-external-id">
            <label>Id: </label>
            <a v-if="lineItem.vendor_mapping.url" :href="item.vendor_mapping.url" target="_black">{{ lineItem.vendor_mapping.id }}</a>
            <span v-else>{{ lineItem.vendor_mapping.id }}</span>
          </div>
          <div v-if="lineItem.vendor_mapping?.sku" class="vendor-mapping-group account-external-id">
            <label>Sku: </label>
            <span>{{ lineItem.vendor_mapping.sku }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="lineItem.alerts && lineItem.alerts.length > 0" class="alert alert-danger">
      <ul>
        <li v-for="(alert, index) in lineItem.alerts" :key="index" v-show="alert.messenger" v-html="`[${this.$util.timestampToDateTime(parseInt(alert.date_created))}] ${alert.messenger}`"></li>
      </ul>
    </div>
    <div v-if="lineItem.actions?.length > 0" class="item-action">
      <i class="ti ti-dots-vertical"></i>
      <div class="list-action break-normal">
        <span v-for="(action, key, index) in lineItem.actions" :key="index" :class="[action.id.replace('.', '-'), action.style || '']">
          <a href="#" :aria-label="action.label" @click.stop.prevent="handleAction(lineItem, action)">
            {{ action.label }}
          </a>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import BodyItemRowAction from '@/components/TableList/Cols/BodyItemRowAction.vue'
export default {
  extends: BodyItemRowAction,
  computed: {
    editable() {
      return this.config?.editable
    },
    lineItem() {
      return this.config?.line_item
    },
    index() {
      return this.config?.index
    }
  },
  methods: {
    renderNote(str) {
      let links = this.$util.getLink(str)
      if (links !== null) {
        links.forEach((link) => {
          if (Object.keys(this.$util.options.extension.image).includes(this.$util.getExtensionFromString(link))) {
            str = str.replace(link, `<div class="attached preview d-flex"><img src="${link}" loading="lazy"><a href="${link}" target="_blank">${link}</a></div>`)
          }
        })
      }
      return str
    },
    handlePreviewClick() {
      this.$emitter.emit('SubModal', {
        name: 'full-screen-image-preview',
        data: {
          files: Object.values(this.lineItem.gallery),
          config: {
            thumbnail: true
          }
        }
      })
    },
    handleLineItemClick() {
      let lineItem = Object.assign({}, this.lineItem, {
        product_index: this.index
      })
      this.$emitter.emit('Modal', {
        name: 'line-item.edit',
        data: lineItem,
        $el: this.$el instanceof HTMLElement ? this.$el.closest('tr.item') : this.$el.parentNode.closest('tr.item')
      })
    }
  },
  props: {
    config: Object
  }
}
</script>
<style lang="scss">
.line-item-thumbnail {
  cursor: pointer;
}
.attached.preview {
  padding-top: 5px;
  img {
    width: 60px;
    height: 60px;
    min-width: 60px;
    min-height: 60px;
    object-fit: contain;
    border: 1px solid #ccc;
    border-radius: var(--border-radius, 0.25rem);
  }
  a {
    padding-left: 10px;
  }
}
.line-item-main {
  .reference {
    margin-top: 5px;
    a {
      height: auto;
      width: auto;
      font-size: 11px;
      cursor: pointer;
      padding: 2px 10px;
      margin: 0 5px 5px 0;
      border-radius: var(--border-radius, 0.25rem);
      display: inline-block;
      color: #333;
      background: #e4e4e4;
    }
  }
}
.line-item-gallery {
  width: 200px;
  .gallery {
    margin: -5px;
    margin-top: 5px;
  }
  .gallery-item {
    width: 60px;
    height: 60px;
    margin: 5px;
    cursor: pointer;
    img {
      width: 100%;
      height: 100%;
      -o-object-fit: cover;
      object-fit: cover;
    }
  }
  .more-thumbnail {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #ddd;
    font-size: 18px;
    font-weight: bold;
    color: #888;
  }
}

.order-note,
.meta-group.note {
  white-space: pre-wrap;
}
</style>
